import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { EditableCell, StatusPill } from '../components/InlineEditTable';
import { useBulkSelect } from '../hooks/useBulkSelect';
import SearchableSelect from '../components/SearchableSelect';
import DataTable from '../components/DataTable';
import ReparentDeleteModal from '../components/ReparentDeleteModal';
import FormField from '../components/FormField';
import { useFormValidation } from '../hooks/useFormValidation';
import { useSoftDelete } from '../hooks/useSoftDelete';
import useSort from '../hooks/useSort';
import Toast from '../components/Toast';
import { useCurrentUser } from '../context/UserContext';
import { getLeadStatuses, LEAD_STATUS_COLORS } from '../utils/leadStatus';
import CollapsibleSection from '../components/CollapsibleSection';
import { buildStatusGroupMap, splitByStatusGroup, buildProgramOptionGroups } from '../utils/statusGroups';

const API = process.env.REACT_APP_API_URL;

const emptyForm = () => ({
  component_name: '', program_id: '', product_id: '', status: '',
  quantity_ordered: '', cost_per: '', flight_date_start: '', flight_date_end: '', notes: '',
  includes_call_review: false,
});

const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '100%', boxSizing: 'border-box' };

const COMPONENT_RULES = {
  component_name: { required: true, label: 'Component Name' },
  program_id: { required: true, label: 'Program' },
  flight_date_start: { required: true, label: 'Flight Start' },
  flight_date_end: { required: true, label: 'Flight End' },
};

export default function Components() {
  const [components, setComponents] = useState([]);
  const [deleteModal, setDeleteModal] = useState(null);  // component being deleted-with-children
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [programs, setPrograms] = useState([]);
  // Closed programs nest into a collapsible section on every Program picker
  // below instead of mixing into the flat list — see src/utils/statusGroups.js.
  const [programStatuses, setProgramStatuses] = useState([]);
  const programOptionGroups = buildProgramOptionGroups(programs, buildStatusGroupMap(programStatuses));
  const [products, setProducts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [targetingLists, setTargetingLists] = useState([]);
  const [filterProgram, setFilterProgram] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const { currentUser } = useCurrentUser();
  const isAdmin = currentUser?.role === 'admin';
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [expandedRow, setExpandedRow]   = useState(null);
  const [expandedTab, setExpandedTab]   = useState({});
  const [showAssignModal, setShowAssignModal] = useState(null);
  const [assignListId, setAssignListId] = useState('');
  // Clone modal (2026-09-02, real bug fix — see handleClone()'s comment below).
  const [cloneModal, setCloneModal] = useState(null);
  const [cloneProgramId, setCloneProgramId] = useState('');
  const [cloneName, setCloneName] = useState('');

  // Leads data: goal_id → {total, by_status}
  const [leadsData, setLeadsData] = useState({});

  // Sort
  // Column-header sort, shared by the active and collapsed-Closed tables via
  // sortRows. No default key — until a header is clicked, both buckets keep
  // splitByStatusGroup's own flight_date_end-desc order.
  const { sortRows, field: sortField, dir: sortDir, toggle: handleSort } = useSort(null, null, 'asc', {
    program_name: c => programName(c.program_id),
    product_name: c => productName(c.product_id),
  });

  const toggleRow = (id, tab) => {
    const defaultTab = 'goals';
    const currentTab = expandedTab[id] || defaultTab;
    if (expandedRow === id && currentTab === tab) {
      setExpandedRow(null);
    } else {
      setExpandedRow(id);
      setExpandedTab(prev => ({ ...prev, [id]: tab }));
    }
  };

  // Fetch (or re-fetch) lead summaries for a set of goal ids. Always overwrites
  // whatever's cached — a lead's status can change elsewhere (e.g. Accept
  // Delivery, a QA override) while this component's data sat cached, so both
  // re-expanding the tab and the manual refresh button must pull live counts,
  // never silently reuse a stale snapshot from earlier in the session.
  const fetchGoalLeadSummaries = useCallback((componentId, goalIds) => {
    if (goalIds.length === 0) return;
    setLeadsData(prev => {
      const upd = { ...prev };
      goalIds.forEach(id => { upd[id] = null; }); // null = loading
      return upd;
    });
    axios.get(`${API}/leads/summary?component_id=${componentId}`)
      .then(res => {
        const map = {};
        res.data.forEach(entry => { map[entry.goal_id] = entry; });
        setLeadsData(prev => {
          const upd = { ...prev };
          goalIds.forEach(id => { upd[id] = map[id] || { total: 0, by_status: {} }; });
          return upd;
        });
      })
      .catch(() => {
        setLeadsData(prev => {
          const upd = { ...prev };
          goalIds.forEach(id => { upd[id] = { total: 0, by_status: {} }; });
          return upd;
        });
      });
  }, []);

  // When goals tab opens for a component, load lead summaries for its goals.
  useEffect(() => {
    if (!expandedRow) return;
    const activeTab = expandedTab[expandedRow] || 'goals';
    if (activeTab !== 'goals') return;
    const comp = components.find(c => c.id === expandedRow);
    if (!comp) return;
    fetchGoalLeadSummaries(comp.id, (comp.goals || []).map(g => g.id));
  }, [expandedRow, expandedTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const { selected, toggle, toggleAll, clear } = useBulkSelect();
  const { errors, validate, reset: resetErrors } = useFormValidation(COMPONENT_RULES);

  // The PUT response is already a fully joined ComponentOut (_load_component) — use it
  // directly instead of a full fetchComponents() refetch of every row for one field.
  const saveField = async (id, patch) => {
    const res = await axios.put(`${API}/components/${id}`, patch);
    setComponents(prev => prev.map(c => c.id === id ? res.data : c));
  };

  const bulkSetStatus = async (status) => {
    await Promise.all([...selected].map(id => axios.put(`${API}/components/${id}`, { status })));
    clear();
    await fetchComponents();
  };

  useEffect(() => {
    axios.get(`${API}/programs/`).then(r => setPrograms(r.data));
    axios.get(`${API}/products/`).then(r => setProducts(r.data));
    axios.get(`${API}/lookup-values/?category=component_status`).then(r => setStatuses(r.data)).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setProgramStatuses(r.data)).catch(() => {});
    axios.get(`${API}/targeting-lists/`).then(r => setTargetingLists(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchComponents(); }, [showDeleted]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchComponents = () => axios.get(`${API}/components/`, { params: { include_deleted: showDeleted } }).then(r => setComponents(r.data));

  const { toast, dismissToast, restore: restoreComponent, deleteWithUndo } = useSoftDelete('component', fetchComponents);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate(form)) return;
    const data = {
      ...form,
      program_id: form.program_id ? parseInt(form.program_id) : null,
      product_id: form.product_id ? parseInt(form.product_id) : null,
      quantity_ordered: form.quantity_ordered !== '' ? parseInt(form.quantity_ordered) : null,
      cost_per: form.cost_per !== '' ? parseFloat(form.cost_per) : null,
      flight_date_start: form.flight_date_start || null,
      flight_date_end: form.flight_date_end || null,
    };
    try {
      if (editing) {
        await axios.put(`${API}/components/${editing}`, data);
        setEditing(null);
      } else {
        await axios.post(`${API}/components/`, data);
      }
      setForm(emptyForm());
      setShowForm(false);
      resetErrors();
      fetchComponents();
    } catch (err) {
      const d = err.response?.data?.detail;
      alert('Could not save component: ' + (Array.isArray(d) ? d.map(x => `${x.loc?.slice(-1)[0]}: ${x.msg}`).join('; ') : (d || err.message)));
    }
  };

  const handleEdit = (c) => {
    setEditing(c.id);
    setForm({
      component_name: c.component_name || '',
      program_id: c.program_id || '',
      product_id: c.product_id || '',
      status: c.status || '',
      quantity_ordered: c.quantity_ordered !== null && c.quantity_ordered !== undefined ? c.quantity_ordered : '',
      cost_per: c.cost_per !== null && c.cost_per !== undefined ? c.cost_per : '',
      flight_date_start: c.flight_date_start || '',
      flight_date_end: c.flight_date_end || '',
      notes: c.notes || '',
      includes_call_review: c.includes_call_review || false,
    });
    setShowForm(true);
    resetErrors();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (c) => {
    if ((c.goals || []).length > 0) {
      setDeleteModal(c);  // has goals → 3-way modal (cascade / move / cancel)
      return;
    }
    if (window.confirm('Delete this component?')) {
      deleteWithUndo(c, c.component_name || 'component', () => axios.delete(`${API}/components/${c.id}`))
        .catch(err => alert('Delete failed: ' + (err.response?.data?.detail || err.message)));
    }
  };

  const doCascadeDelete = async () => {
    setDeleteBusy(true);
    try {
      const c = deleteModal;
      await deleteWithUndo(c, c.component_name || 'component', () => axios.delete(`${API}/components/${c.id}`));
      setDeleteModal(null);
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.detail || err.message));
    } finally { setDeleteBusy(false); }
  };

  const doReparentDelete = async (moves) => {
    setDeleteBusy(true);
    try {
      const c = deleteModal;
      await deleteWithUndo(c, c.component_name || 'component', () => axios.post(`${API}/components/${c.id}/delete-with-reparent`, { moves }));
      setDeleteModal(null);
    } catch (err) {
      alert('Move & delete failed: ' + (err.response?.data?.detail || err.message));
    } finally { setDeleteBusy(false); }
  };

  // Opens a modal to pick the clone's name + target program, defaulting to
  // this component's own program (2026-09-02, real bug fix). Previously
  // cloned instantly and silently, always onto the SAME program_id as the
  // source, with no way to redirect it elsewhere and no confirmation of
  // which program it landed in — real report: a PM cloned a component
  // meaning to set up a brand-new program from an existing similar one, the
  // clone silently stayed on the OLD program instead, and it never showed
  // up in the new program's dropdowns anywhere (Lead QA Review, Goals, …)
  // because it was never actually created there — renaming it didn't help,
  // since renaming doesn't move it to a different program either. The PM
  // worked around it by deleting the misplaced clone and creating the
  // component by hand directly under the new program.
  const handleClone = (c) => {
    setCloneModal(c);
    setCloneProgramId(String(c.program_id));
    setCloneName((c.component_name || 'Component') + ' (copy)');
  };

  const confirmClone = async () => {
    const c = cloneModal;
    const data = {
      component_name: cloneName.trim() || (c.component_name || 'Component') + ' (copy)',
      program_id: parseInt(cloneProgramId, 10),
      product_id: c.product_id,
      status: c.status,
      quantity_ordered: c.quantity_ordered,
      cost_per: c.cost_per,
      flight_date_start: c.flight_date_start,
      flight_date_end: c.flight_date_end,
      notes: c.notes,
      includes_call_review: c.includes_call_review || false,
    };
    await axios.post(`${API}/components/`, data);
    setCloneModal(null);
    fetchComponents();
  };

  const handleAssignTargeting = async () => {
    if (!assignListId) return;
    await axios.post(`${API}/components/${showAssignModal}/targeting`, { targeting_list_id: parseInt(assignListId) });
    setShowAssignModal(null);
    setAssignListId('');
    fetchComponents();
  };

  const handleRemoveTargeting = async (compId, assignmentId) => {
    await axios.delete(`${API}/components/${compId}/targeting/${assignmentId}`);
    fetchComponents();
  };

  const programName = (id) => programs.find(p => p.id === id)?.program_name || '—';
  const productName = (id) => products.find(p => p.id === id)?.name || '—';

  const filtered = components.filter(c =>
    (!filterProgram || String(c.program_id) === filterProgram) &&
    (!filterStatus || c.status === filterStatus)
  );

  // Inline-edit option lists
  const programOpts = programs.map(p => ({ value: p.id, label: p.program_name }));
  const productOpts = [{ value: '', label: '—' }, ...products.map(p => ({ value: p.id, label: p.name }))];
  const statusOpts = [{ value: '', label: '—' }, ...statuses.map(s => ({ value: s.value, label: s.value }))];

  // Pending/Running always visible, Closed collapsed at the bottom — classification
  // comes from lookup_values.status_group (admin-editable), not a hardcoded status
  // list. An explicit column-header sort click still applies within each bucket; the
  // group split and the Closed collapse stay in effect either way.
  const statusGroupMap = buildStatusGroupMap(statuses);
  const { active: activeBase, closed: closedBase } = splitByStatusGroup(
    filtered, { statusKey: 'status', groupMap: statusGroupMap, dateKey: 'flight_date_end' }
  );
  const sorted = sortRows(activeBase);
  const sortedClosed = sortRows(closedBase);

  const columns = [
    { key: 'program_name', header: 'Program', width: 160, sortable: true, render: c =>
        <EditableCell type="select" value={c.program_id} options={programOpts} display={programName(c.program_id)}
          onSave={v => saveField(c.id, { program_id: v ? parseInt(v) : null })} /> },
    { key: 'component_name', header: 'Component', width: 180, sortable: true, render: c =>
        <EditableCell value={c.component_name} display={<strong style={{ color: '#2c3e50' }}>{c.component_name || '(unnamed)'}</strong>}
          onSave={v => saveField(c.id, { component_name: v })} /> },
    { key: 'product_name', header: 'Product', width: 130, sortable: true, render: c =>
        <EditableCell type="select" value={c.product_id} options={productOpts} display={productName(c.product_id)}
          onSave={v => saveField(c.id, { product_id: v ? parseInt(v) : null })} /> },
    { key: 'status', header: 'Status', width: 110, sortable: true, render: c =>
        <EditableCell type="select" value={c.status} options={statusOpts}
          display={<StatusPill value={c.status} lookups={statuses} fallback="#3498db" />}
          onSave={v => saveField(c.id, { status: v })} /> },
    { key: 'quantity_ordered', header: 'Qty / Goal Sum', width: 100, sortable: true, render: c => {
        const goalSumQty = (c.goals || []).reduce((s, g) => s + (g.quantity_ordered || 0), 0);
        return <EditableCell type="number" value={c.quantity_ordered}
          display={<>
            {c.quantity_ordered ?? '—'}
            {goalSumQty > 0 && (
              <span style={{ marginLeft: '6px', fontSize: '11px', color: goalSumQty === c.quantity_ordered ? '#27ae60' : '#e67e22', fontWeight: '600' }} title="Sum of all goal quantities">({goalSumQty})</span>
            )}
          </>}
          onSave={v => saveField(c.id, { quantity_ordered: v === '' ? null : parseInt(v) })} />;
      } },
    { key: 'cost_per', header: 'Cost/Lead', width: 90, sortable: true, render: c =>
        <EditableCell type="number" step="0.01" value={c.cost_per} display={c.cost_per ? '$' + c.cost_per : undefined}
          onSave={v => saveField(c.id, { cost_per: v === '' ? null : parseFloat(v) })} /> },
    { key: 'revenue', header: 'Revenue', width: 100, render: c => {
        const rev = (c.quantity_ordered && c.cost_per) ? (c.quantity_ordered * c.cost_per) : null;
        return rev ? '$' + rev.toLocaleString() : '—';
      } },
    { key: 'flight_date_start', header: 'Flight Start', width: 150, sortable: true, render: c =>
        <EditableCell type="date" value={c.flight_date_start} onSave={v => saveField(c.id, { flight_date_start: v || null })} /> },
    { key: 'flight_date_end', header: 'Flight End', width: 150, sortable: true, render: c =>
        <EditableCell type="date" value={c.flight_date_end} onSave={v => saveField(c.id, { flight_date_end: v || null })} /> },
    { key: 'actions', header: 'Actions', width: 250, nowrap: true, render: c => {
        if (c.deleted_at) {
          return <button onClick={() => restoreComponent(c)} style={{ padding: '4px 8px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Restore</button>;
        }
        const rowOpen = expandedRow === c.id;
        const activeTab = expandedTab[c.id] || 'goals';
        const btnTab = (tab) => ({ padding: '4px 10px', fontSize: '12px', border: 'none', cursor: 'pointer', borderRadius: '4px', background: (rowOpen && activeTab === tab) ? '#2c3e50' : '#e8ecef', color: (rowOpen && activeTab === tab) ? 'white' : '#555' });
        return (
          <>
            <button onClick={() => toggleRow(c.id, 'goals')}     style={{ ...btnTab('goals'),     marginRight: '3px' }}>📋 Goals</button>
            <button onClick={() => toggleRow(c.id, 'targeting')} style={{ ...btnTab('targeting'), marginRight: '3px' }}>🎯</button>
            <button onClick={() => handleClone(c)} style={{ marginRight: '3px', padding: '4px 8px', background: '#e67e22', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Clone</button>
            <button onClick={() => handleEdit(c)}  style={{ marginRight: '3px', padding: '4px 8px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
            <button onClick={() => handleDelete(c)} style={{ padding: '4px 8px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Del</button>
          </>
        );
      } },
  ];

  const panelWrap = { padding: '12px 20px', background: '#f8fbff', borderBottom: '2px solid #e0ecff' };

  const renderExpanded = (c) => {
    if (expandedRow !== c.id) return null;
    const activeTab = expandedTab[c.id] || 'goals';
    if (activeTab === 'targeting') {
      return (
        <div style={panelWrap}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>Staged Targeting Lists</div>
          <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '10px' }}>
            These lists will be offered for assignment when new goals are created under this component.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
            {(c.targeting_assignments || []).map(ta => (
              <span key={ta.id} style={{ background: '#eaf2fb', color: '#2c3e50', borderRadius: '12px', padding: '4px 10px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                {ta.targeting_list?.list_name}
                <span style={{ fontSize: '10px', background: '#3498db', color: 'white', borderRadius: '8px', padding: '1px 5px' }}>{ta.targeting_list?.list_type}</span>
                <button onClick={() => handleRemoveTargeting(c.id, ta.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', fontSize: '12px', padding: 0, lineHeight: 1 }}>×</button>
              </span>
            ))}
            {(c.targeting_assignments || []).length === 0 && <span style={{ color: '#aaa', fontSize: '12px' }}>No staged lists</span>}
          </div>
          <button onClick={() => { setShowAssignModal(c.id); setAssignListId(''); }}
            style={{ padding: '5px 12px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
            + Stage a List
          </button>
        </div>
      );
    }
    return (
      <div style={panelWrap}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#555' }}>Goals ({(c.goals || []).length})</div>
          {(c.goals || []).length > 0 && (
            <button onClick={() => fetchGoalLeadSummaries(c.id, c.goals.map(g => g.id))}
              title="Reload lead counts — useful after accepting a supplier delivery or other QA changes made elsewhere"
              style={{ padding: '3px 10px', background: 'white', border: '1px solid #95a5a6', color: '#555', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
              🔄 Refresh
            </button>
          )}
        </div>
        {(c.goals || []).length === 0 ? (
          <div style={{ color: '#aaa', fontSize: '12px' }}>No goals for this component.</div>
        ) : (
          <table style={{ borderCollapse: 'collapse', fontSize: '12px', width: '100%' }}>
            <thead>
              <tr style={{ background: '#eaf4fb' }}>
                {['Goal Name', 'Status', 'Qty Ordered', 'Spend/Lead', 'Flight Start', 'Flight End', 'Leads'].map(h => (
                  <th key={h} style={{ padding: '5px 10px', textAlign: 'left', borderBottom: '1px solid #ccc' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {c.goals.map(g => {
                const ls = leadsData[g.id];
                return (
                  <tr key={g.id}>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #eee' }}>{g.goal_name || '—'}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #eee' }}>{g.status || '—'}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #eee' }}>{g.quantity_ordered ?? '—'}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #eee' }}>{g.spend_per ? '$' + g.spend_per : '—'}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #eee' }}>{g.flight_date_start || '—'}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #eee' }}>{g.flight_date_end || '—'}</td>
                    <td style={{ padding: '5px 10px', borderBottom: '1px solid #eee' }}>
                      {ls === undefined ? (
                        <span style={{ color: '#ccc' }}>—</span>
                      ) : ls === null ? (
                        <span style={{ color: '#aaa' }}>Loading…</span>
                      ) : ls.total === 0 ? (
                        <span style={{ color: '#bbb' }}>No leads</span>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {getLeadStatuses(true).filter(s => ls.by_status[s]).map(s => (
                            <span key={s} style={{ background: LEAD_STATUS_COLORS[s]?.color, color: 'white', borderRadius: '4px', padding: '1px 6px', fontSize: '10px' }}>
                              {s.split(' ')[0]}: {ls.by_status[s]}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Components</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <SearchableSelect value={filterProgram} onChange={setFilterProgram}
            placeholder="All Programs"
            options={programOptionGroups.options}
            closedOptions={programOptionGroups.closedOptions} />
          <SearchableSelect value={filterStatus} onChange={setFilterStatus}
            placeholder="All Statuses"
            options={statuses.map(s => ({ value: s.value, label: s.value }))} />
          {isAdmin && (
            <label style={{ fontSize: '13px', color: '#555', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
              <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} />
              Show deleted
            </label>
          )}
          <button onClick={() => { setShowForm(true); setEditing(null); setForm(emptyForm()); resetErrors(); }}
            style={{ padding: '8px 16px', background: '#2c3e50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            + New Component
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 16px' }}>{editing ? 'Edit Component' : 'New Component'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <FormField label="Component Name" required error={errors.component_name}>
              <input value={form.component_name} onChange={e => setForm({ ...form, component_name: e.target.value })} style={inputStyle} />
            </FormField>
            <FormField label="Program" required error={errors.program_id}>
              <SearchableSelect value={form.program_id} onChange={v => setForm({ ...form, program_id: v })}
                options={programOptionGroups.options} closedOptions={programOptionGroups.closedOptions}
                placeholder="Select program…" />
            </FormField>
            <FormField label="Product">
              <select value={form.product_id} onChange={e => setForm({ ...form, product_id: e.target.value })} style={inputStyle}>
                <option value="">Select product…</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </FormField>
            <FormField label="Status">
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={inputStyle}>
                <option value="">Select…</option>
                {statuses.map(s => <option key={s.id} value={s.value}>{s.value}</option>)}
              </select>
            </FormField>
            <FormField label="Qty Ordered">
              <input type="number" value={form.quantity_ordered} onChange={e => setForm({ ...form, quantity_ordered: e.target.value })} style={inputStyle} />
            </FormField>
            <FormField label="Cost/Lead">
              <input type="number" step="0.01" value={form.cost_per} onChange={e => setForm({ ...form, cost_per: e.target.value })} style={inputStyle} />
            </FormField>
            <FormField label="Flight Start" required error={errors.flight_date_start}>
              <input type="date" value={form.flight_date_start} onChange={e => setForm({ ...form, flight_date_start: e.target.value })} style={inputStyle} />
            </FormField>
            <FormField label="Flight End" required error={errors.flight_date_end}>
              <input type="date" value={form.flight_date_end} onChange={e => setForm({ ...form, flight_date_end: e.target.value })} style={inputStyle} />
            </FormField>
            <FormField label="Includes Call Review">
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#555', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!form.includes_call_review}
                  onChange={e => setForm({ ...form, includes_call_review: e.target.checked })} />
                Adds an "Approved Pending Call Review" status between Under Review and DemandArc Accepted
              </label>
            </FormField>
            <FormField label="Notes" gridColumn="span 2">
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                style={{ ...inputStyle, height: '60px', resize: 'vertical' }} />
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" style={{ padding: '8px 16px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              {editing ? 'Update' : 'Create'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); setForm(emptyForm()); resetErrors(); }}
              style={{ padding: '8px 16px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <DataTable
        rows={sorted}
        columns={columns}
        sort={{ field: sortField, dir: sortDir, onSort: handleSort }}
        bulk={{ selected, toggle, toggleAll, statuses, onApply: bulkSetStatus }}
        renderExpanded={renderExpanded}
        rowStyle={c => ({ background: c.deleted_at ? '#f7f7f7' : ((expandedRow === c.id || selected.has(c.id)) ? '#f0f7ff' : 'white'), opacity: c.deleted_at ? 0.6 : 1 })}
        emptyText="No components found."
      />
      {sortedClosed.length > 0 && (
        <CollapsibleSection label="Closed" count={sortedClosed.length} bodyStyle={{ padding: 0 }}>
          <DataTable
            rows={sortedClosed}
            columns={columns}
            sort={{ field: sortField, dir: sortDir, onSort: handleSort }}
            bulk={{ selected, toggle, toggleAll, statuses, onApply: bulkSetStatus }}
            renderExpanded={renderExpanded}
            rowStyle={c => ({ background: c.deleted_at ? '#f7f7f7' : ((expandedRow === c.id || selected.has(c.id)) ? '#f0f7ff' : 'white'), opacity: c.deleted_at ? 0.6 : 1 })}
            emptyText="No closed components."
          />
        </CollapsibleSection>
      )}

      {showAssignModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '24px', width: '380px' }}>
            <h3 style={{ margin: '0 0 16px' }}>Assign Targeting List</h3>
            <select value={assignListId} onChange={e => setAssignListId(e.target.value)} style={{ ...inputStyle, marginBottom: '16px' }}>
              <option value="">Select a list…</option>
              {targetingLists.map(tl => <option key={tl.id} value={tl.id}>{tl.list_name} ({tl.list_type})</option>)}
            </select>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleAssignTargeting} disabled={!assignListId} style={{ padding: '8px 16px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Assign</button>
              <button onClick={() => setShowAssignModal(null)} style={{ padding: '8px 16px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {cloneModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '24px', width: '420px' }}>
            <h3 style={{ margin: '0 0 16px' }}>Clone "{cloneModal.component_name}"</h3>
            <label style={{ fontSize: '12px', color: '#555', display: 'block', marginBottom: '4px' }}>New component name</label>
            <input value={cloneName} onChange={e => setCloneName(e.target.value)} style={{ ...inputStyle, marginBottom: '14px' }} />
            <label style={{ fontSize: '12px', color: '#555', display: 'block', marginBottom: '4px' }}>Clone into program</label>
            <SearchableSelect value={cloneProgramId} onChange={setCloneProgramId}
              options={programOptionGroups.options} closedOptions={programOptionGroups.closedOptions}
              placeholder="Select program…" />
            <div style={{ fontSize: '11px', color: '#888', margin: '6px 0 16px' }}>
              Defaults to this component's own program — pick a different one to clone it onto
              another program instead (e.g. a new program you just set up).
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={confirmClone} disabled={!cloneProgramId || !cloneName.trim()} style={{ padding: '8px 16px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Clone</button>
              <button onClick={() => setCloneModal(null)} style={{ padding: '8px 16px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {deleteModal && (
        <ReparentDeleteModal
          entityName={deleteModal.component_name || `Component #${deleteModal.id}`}
          parentNoun="component"
          childNoun="goals"
          items={(deleteModal.goals || []).map(g => ({ id: g.id, name: g.goal_name || `Goal #${g.id}` }))}
          targets={components.filter(x => x.id !== deleteModal.id).map(x => ({ value: x.id, label: x.component_name || `Component #${x.id}` }))}
          childIdKey="goal_id"
          targetIdKey="target_component_id"
          onCascade={doCascadeDelete}
          onMove={doReparentDelete}
          onClose={() => setDeleteModal(null)}
          busy={deleteBusy}
        />
      )}
      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
}
